import {
    parseISO,
    eachDayOfInterval,
    eachWeekOfInterval,
    min,
    max,
    format,
    differenceInHours,
    differenceInMinutes,
    endOfMonth,
    startOfMonth,
    endOfDay,
    startOfDay,
    getDay,
} from "date-fns";
import { TimeOff, WorkLog, StaffWeeklyHours, DayOffType, MonthlyFigureOptions } from "../types";
import { utcToZonedTime } from "date-fns-tz";
import { TIMEZONE } from "../../../utils/constants";
import { convertToHours } from "./general";
import { Holiday } from "../../../api/usePortugalHolidays";
import { getWeekEndDate, getWeekStartDate, isBetweenDates, isDateBefore, isDateBeforeOrSameDay } from "../../../utils/dateUtils";
import { ContractRun, getRunLabel, resolveRunIndexForDate } from "./employmentRun";
import { sortBy } from "lodash";

export const calculateDayStats = (logs: WorkLog[]) => {
    if (logs.length === 0) return { timeStart: "", timeEnd: "", workHours: 0, clientsHours: 0, timeOffHours: 0 };

    const timeStart = min(logs.map((entry) => parseISO(entry.start_date)));
    const timeEnd = max(logs.map((entry) => parseISO(entry.end_date)));

    const { clientsStart, clientsEnd } = clientsStartAndEnd(logs);
    const workTotals = calculateHoursTotals(logs);

    return {
        timeStart: format(timeStart, "HH:mm"),
        timeEnd: format(timeEnd, "HH:mm"),
        workHours: workTotals.totalWorkHours,
        clientsHours: workTotals.totalClientHours,
        clientsStart,
        clientsEnd,
    };
};

const clientsStartAndEnd = (logs: WorkLog[]) => {
    const clientsLogs = logs.filter((entry) => entry.type === "clients");

    if (clientsLogs.length === 0) {
        return { clientsStart: null, clientsEnd: null };
    }

    const clientsStartDate = min(clientsLogs.map((entry) => parseISO(entry.start_date)));
    const clientsEndDate = max(clientsLogs.map((entry) => parseISO(entry.end_date)));

    const clientsStart = { time: format(clientsStartDate, "HH:mm") };
    const clientsEnd = { time: format(clientsEndDate, "HH:mm") };

    return { clientsStart, clientsEnd };
};

export const calculateHours = (start: string, end: string) => {
    const startDate = utcToZonedTime(parseISO(start), TIMEZONE);
    const endDate = utcToZonedTime(parseISO(end), TIMEZONE);
    const hours = differenceInHours(endDate, startDate);
    const minutes = differenceInMinutes(endDate, startDate) % 60;
    return `${hours}h ${minutes}m`;
};
// windowStart/windowEnd bound the figures to the current relationship: rostered days outside it (e.g.
// shifts left on the calendar after a leaver's last day) must not count hours nor meal-subsidy days.
export const applyMonthlyFigureBounds = (entries: any[], options?: MonthlyFigureOptions) => {
    const capToMonth = options?.capToMonth instanceof Date ? options.capToMonth : null;
    const windowStart = options?.windowStart instanceof Date ? options.windowStart : null;
    const windowEnd = options?.windowEnd instanceof Date ? options.windowEnd : null;
    const boundedEntries = entries.filter((entry) => {
        const entryDate = parseISO(entry.start_date);
        if (capToMonth && !isBetweenDates(entryDate, startOfMonth(capToMonth), endOfMonth(capToMonth))) return false;
        if (windowStart && entryDate < windowStart) return false;
        if (windowEnd && entryDate > windowEnd) return false;
        return true;
    });
    return boundedEntries;
};

export const calculateHoursTotals = (entries: any[], options?: MonthlyFigureOptions) => {
    const filteredEntries = applyMonthlyFigureBounds(entries, options);
    const types = ["schedules", "clients"];
    const totalsPerType = types.reduce((totals: Record<string, number>, type) => {
        totals[type] = filteredEntries
            .filter((entry) => entry.type === type)
            .reduce((sum, entry) => sum + convertToHours(calculateHours(entry.start_date, entry.end_date)), 0);
        return totals;
    }, {});
    return {
        totalWorkHours: totalsPerType.schedules,
        effectiveWorkHours: totalsPerType.schedules,
        totalClientHours: totalsPerType.clients,
    };
};

export const countWorkDaysOfAtLeastFiveHours = (workLogs: Array<WorkLog>, options?: MonthlyFigureOptions) => {
    const perDayTotals = new Map<string, number>();
    const filteredLogs = applyMonthlyFigureBounds(workLogs, options);

    for (const entry of filteredLogs) {
        if (entry.type !== "schedules") continue;

        const startDate = utcToZonedTime(parseISO(entry.start_date), TIMEZONE);

        const key = format(startDate, "yyyy-MM-dd");
        const previousTotal = perDayTotals.get(key) || 0;
        const entryHours = convertToHours(calculateHours(entry.start_date, entry.end_date));
        perDayTotals.set(key, previousTotal + entryHours);
    }

    const qualifyingDaysCount = Array.from(perDayTotals.values()).filter((hours) => hours >= 5).length;
    return qualifyingDaysCount;
};

export const calculateTimeOffsSummary = (
    firstDayOfMonth: Date,
    lastDayOfMonth: Date,
    timeOffs: Array<TimeOff>,
    portugalHolidays: Array<Holiday>,
): TimeOffsMonthSummary => {
    const firstDayOfWeek = getWeekStartDate(firstDayOfMonth);
    const lastDayOfWeek = endOfDay(getWeekEndDate(lastDayOfMonth));
    const summary = {
        daysPublicHolidays: new Set<string>(),
        daysDayoff: new Set<string>(),
        daysHoliday: new Set<string>(),
        hoursSickleave: 0,
        hoursMissedJustify: 0,
        hoursMissedNotJustify: 0,
        daysWithHoursOffTotalExceptPublicHolidays: new Map<string, number>(),
        hoursMissedCurrentMonthTotal: 0,
        hoursSickLeaveCurrentMonthTotal: 0,
        daysSickLeaveCurrentMonthTotal: new Set<string>(),
        daysHolidayCurrentMonthTotal: new Set<string>(),
    };

    const defaultDayOffHours = 8;

    for (const holiday of portugalHolidays) {
        const holidayZoned = utcToZonedTime(parseISO(holiday.date), TIMEZONE);
        const holidayDateString = format(holidayZoned, "yyyy-MM-dd");
        if (holidayZoned >= firstDayOfWeek && holidayZoned <= lastDayOfWeek) {
            summary.daysPublicHolidays.add(holidayDateString);
        }
    }

    for (const entry of timeOffs) {
        const entryStart = parseISO(entry.start_date);
        const entryEnd = parseISO(entry.end_date);

        // Check if entry belongs within the month's full weeks
        if (entryEnd < firstDayOfWeek || entryStart > lastDayOfWeek) continue;
        const clampedStart = entryStart < firstDayOfWeek ? firstDayOfWeek : entryStart;
        const clampedEnd = entryEnd > lastDayOfWeek ? lastDayOfWeek : entryEnd;
        const hoursDecimal = convertToHours(calculateHours(clampedStart.toISOString(), clampedEnd.toISOString()));

        if (entry.type === "holiday") {
            const dayString = format(utcToZonedTime(entryStart, TIMEZONE), "yyyy-MM-dd");
            summary.daysHoliday.add(dayString);
            if (isBetweenDates(entryStart, firstDayOfMonth, lastDayOfMonth)) {
                summary.daysHolidayCurrentMonthTotal.add(dayString);
            }
        } else if (entry.type === "public_holiday") {
            summary.daysPublicHolidays.add(format(utcToZonedTime(entryStart, TIMEZONE), "yyyy-MM-dd"));
        } else if (entry.type === "day_off") {
            summary.daysDayoff.add(format(utcToZonedTime(entryStart, TIMEZONE), "yyyy-MM-dd"));
        } else if (entry.type === "missed_justified" || entry.type === "missed_not_justified" || entry.type === "sick_leave") {
            if (entry.type === "missed_justified") {
                summary.hoursMissedJustify += hoursDecimal;
                if (isBetweenDates(entryStart, firstDayOfMonth, lastDayOfMonth)) {
                    summary.hoursMissedCurrentMonthTotal += hoursDecimal;
                }
            } else if (entry.type === "missed_not_justified") {
                summary.hoursMissedNotJustify += hoursDecimal;
                if (isBetweenDates(entryStart, firstDayOfMonth, lastDayOfMonth)) {
                    summary.hoursMissedCurrentMonthTotal += hoursDecimal;
                }
            } else if (entry.type === "sick_leave") {
                summary.hoursSickleave += hoursDecimal;
                if (isBetweenDates(entryStart, firstDayOfMonth, lastDayOfMonth)) {
                    summary.daysSickLeaveCurrentMonthTotal.add(format(utcToZonedTime(entryStart, TIMEZONE), "yyyy-MM-dd"));
                    summary.hoursSickLeaveCurrentMonthTotal += hoursDecimal;
                }
            }
        }
        if (entry.type === "day_off") continue;
        const existingHours = summary.daysWithHoursOffTotalExceptPublicHolidays.get(format(clampedStart, "yyyy-MM-dd")) || 0;
        const hoursToAdd =
            entry.type === "missed_not_justified" || entry.type === "missed_justified" || entry.type === "sick_leave"
                ? hoursDecimal
                : defaultDayOffHours;
        summary.daysWithHoursOffTotalExceptPublicHolidays.set(format(clampedStart, "yyyy-MM-dd"), existingHours + hoursToAdd);
    }

    return {
        daysPublicHolidays: summary.daysPublicHolidays,
        daysDayoff: summary.daysDayoff,
        daysHoliday: summary.daysHoliday,
        hoursSickleave: summary.hoursSickleave,
        hoursMissedJustify: summary.hoursMissedJustify,
        hoursMissedNotJustify: summary.hoursMissedNotJustify,
        daysWithHoursOffTotalExceptPublicHolidays: summary.daysWithHoursOffTotalExceptPublicHolidays,
        hoursMissedCurrentMonthTotal: summary.hoursMissedCurrentMonthTotal,
        hoursSickLeaveCurrentMonthTotal: summary.hoursSickLeaveCurrentMonthTotal,
        daysHolidayCurrentMonthTotal: summary.daysHolidayCurrentMonthTotal.size,
        daysSickLeaveCurrentMonthTotal: summary.daysSickLeaveCurrentMonthTotal.size,
    };
};
type TimeOffsMonthSummary = {
    daysPublicHolidays: Set<string>;
    daysDayoff: Set<string>;
    daysHoliday: Set<string>;
    hoursSickleave: number;
    hoursMissedJustify: number;
    hoursMissedNotJustify: number;
    daysWithHoursOffTotalExceptPublicHolidays: Map<string, number>;
    hoursMissedCurrentMonthTotal: number;
    hoursSickLeaveCurrentMonthTotal: number;
    daysHolidayCurrentMonthTotal: number;
    daysSickLeaveCurrentMonthTotal: number;
};

export const calculateHolidaysTotal = (timeoffs: Array<any>) => {
    const uniqueHolidayDays = new Set<string>();

    timeoffs
        .filter((item) => item?.type === "holiday")
        .forEach((entry) => {
            if (!entry?.start_date) return;

            // Each entry represents a single day, use start_date to identify it
            const dayDate = startOfDay(utcToZonedTime(parseISO(entry.start_date), TIMEZONE));
            const dayKey = format(dayDate, "yyyy-MM-dd");
            uniqueHolidayDays.add(dayKey);
        });

    const totalHolidays = uniqueHolidayDays.size;
    return { totalHolidays };
};

// The applicable weekly-hours row for a date: the last row whose date_start <= date (overlaps → last wins),
// unless that row had already ENDED — a day inside a calendar gap (left and returned later) belongs to no
// row, so it charges no expected hours and credits nothing. This is the per-day source for both the rate
// and the day-off pattern.
export const getApplicableWeeklyHoursRow = (date: Date, weeklyWorkHoursSchedule: Array<StaffWeeklyHours>) => {
    const sortedSchedule = sortBy(weeklyWorkHoursSchedule, (entry) => entry.date_start);
    let applicableRow: StaffWeeklyHours | null = null;
    for (const entry of sortedSchedule) {
        const entryStart = parseISO(entry.date_start);
        if (isDateBeforeOrSameDay(entryStart, date)) {
            applicableRow = entry;
        }
    }
    if (!applicableRow) return null;
    const rowEndedBeforeDate = applicableRow.date_end
        ? isDateBefore(endOfDay(parseISO(applicableRow.date_end)), date)
        : false;
    const result = rowEndedBeforeDate ? null : applicableRow;
    return result;
};

export const getApplicableWeeklyHours = (date: Date, weeklyWorkHoursSchedule: Array<StaffWeeklyHours>) => {
    const applicableRow = getApplicableWeeklyHoursRow(date, weeklyWorkHoursSchedule);
    const applicableHours = applicableRow?.weekly_hours ?? 0;
    return applicableHours;
};

export type DayOffConfig = { dayOffType: DayOffType; daysOff: Set<number> };

const WEEKDAY_NUMBER_BY_KEY: Record<string, number> = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
};

export const isValidDayOffType = (value: unknown): value is DayOffType =>
    value === "fixed" || value === "flexible";

// Weekday keys (monday..saturday) → weekday numbers (Mon=1..Sat=6). Directus serialises a CSV field as an
// array, so accept an array directly; also tolerate a comma-separated string. Sunday and unknown tokens are
// ignored; null/blank → empty set. Tolerant of whitespace and case.
export const parseFixedDaysOff = (value: Array<string> | string | null | undefined) => {
    const days = new Set<number>();
    if (!value) return days;
    const tokens = Array.isArray(value) ? value : value.split(",");
    for (const token of tokens) {
        const key = String(token).trim().toLowerCase();
        const weekdayNumber = WEEKDAY_NUMBER_BY_KEY[key];
        if (weekdayNumber !== undefined) days.add(weekdayNumber);
    }
    return days;
};

// The day-off pattern applicable to a date, resolved from its weekly-hours row. null when no row applies
// or the row has no valid day_off_type (→ the caller shows the "missing config" error).
export const getApplicableDayOffConfig = (
    date: Date,
    weeklyWorkHoursSchedule: Array<StaffWeeklyHours>,
): DayOffConfig | null => {
    const applicableRow = getApplicableWeeklyHoursRow(date, weeklyWorkHoursSchedule);
    if (!applicableRow || !isValidDayOffType(applicableRow.day_off_type)) return null;
    const config = { dayOffType: applicableRow.day_off_type, daysOff: parseFixedDaysOff(applicableRow.fixed_days_off) };
    return config;
};

// flexible → 5 (Mon–Fri); fixed → Mon–Sat (6) minus the fixed days off, never below 0.
export const getWorkingDaysCount = (config: DayOffConfig) => {
    if (config.dayOffType === "flexible") return 5;
    const workingDaysCount = Math.max(0, 6 - config.daysOff.size);
    return workingDaysCount;
};

// Sunday is never a working day. flexible works Mon–Fri; fixed works Mon–Sat minus its days off.
export const isWorkingDay = (date: Date, config: DayOffConfig) => {
    const dayOfWeek = getDay(date);
    if (dayOfWeek === 0) return false;
    if (config.dayOffType === "flexible") {
        const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
        return isWeekday;
    }
    const isFixedWorkingDay = dayOfWeek >= 1 && dayOfWeek <= 6 && !config.daysOff.has(dayOfWeek);
    return isFixedWorkingDay;
};

// A working day is worth weekly_hours ÷ working-days. Guards against NaN: 0 when there is no rate, no valid
// config, or zero working days.
export const getDailyEquivalent = (date: Date, weeklyWorkHoursSchedule: Array<StaffWeeklyHours>) => {
    const applicableRow = getApplicableWeeklyHoursRow(date, weeklyWorkHoursSchedule);
    const weeklyHours = applicableRow?.weekly_hours ?? 0;
    const config = getApplicableDayOffConfig(date, weeklyWorkHoursSchedule);
    const workingDaysCount = config ? getWorkingDaysCount(config) : 0;
    const dailyEquivalent = weeklyHours === 0 || workingDaysCount === 0 ? 0 : weeklyHours / workingDaysCount;
    return dailyEquivalent;
};

// Whether a vacation/holiday day earns its daily-equivalent credit. For `fixed` staff we credit only on
// their working days, so a vacation on a fixed day off is neutral (no phantom positive). For `flexible`
// staff the day off is unknown, so we keep the previous behaviour and credit any day.
export const isTimeOffCreditableDay = (date: Date, config: DayOffConfig | null) => {
    if (!config) return false;
    if (config.dayOffType === "flexible") return true;
    const creditable = isWorkingDay(date, config);
    return creditable;
};

// Drives the UI gate: true only when every weekly-hours row applicable across [fromDate, toDate] has a valid
// day_off_type and (when fixed) more than zero working days. No applicable row → false (missing config).
export const hasValidDayOffConfig = (
    weeklyWorkHoursSchedule: Array<StaffWeeklyHours>,
    fromDate: Date,
    toDate: Date,
) => {
    const rowAtStart = getApplicableWeeklyHoursRow(fromDate, weeklyWorkHoursSchedule);
    const applicableRows: Array<StaffWeeklyHours> = rowAtStart ? [rowAtStart] : [];
    const sortedSchedule = sortBy(weeklyWorkHoursSchedule, (entry) => entry.date_start);
    for (const entry of sortedSchedule) {
        const entryStart = parseISO(entry.date_start);
        if (entryStart > fromDate && entryStart <= toDate) applicableRows.push(entry);
    }
    if (applicableRows.length === 0) return false;
    const allValid = applicableRows.every((row) => {
        if (!isValidDayOffType(row.day_off_type)) return false;
        const config = { dayOffType: row.day_off_type, daysOff: parseFixedDaysOff(row.fixed_days_off) };
        const isWindowRowValid = getWorkingDaysCount(config) > 0;
        return isWindowRowValid;
    });
    return allValid;
};

// Extra hours settled in money belong to the window they were paid in: payments for months that closed
// before the window start were settled in the previous relationship and must not be deducted again.
export const sumExtraHoursPaidWithinWindow = (payments: Array<any>, windowStart: Date) => {
    const total = payments.reduce((sum, payment) => {
        const paymentMonthEnd = endOfMonth(new Date(payment.year, payment.month - 1, 1));
        if (paymentMonthEnd < windowStart) return sum;
        return sum + (payment.extra_hours_paid || 0);
    }, 0);
    return total;
};

export const resolveCumulativeFromDate = (windowStart: Date, employmentStartDate: Date | null) => {
    const fromDate = max([windowStart, employmentStartDate ?? windowStart]);
    return fromDate;
};

// Window end, aligned to whole Mon–Sun weeks: the Sunday closing the week that holds the selected
// month's last day. This keeps a month-straddling work-week intact (no boundary split) and is shared
// by the headline and the modal so they always agree. March 2026 → Sun 5 Apr 2026.
export const getCumulativeToDate = (selectedMonthDate: Date) => {
    const monthEnd = endOfMonth(selectedMonthDate);
    const toDate = getWeekEndDate(monthEnd);
    return toDate;
};

// The end of the working relationship, taken from the latest weekly-hours row's `date_end` (end of that
// day). When set, the bag stops there — this is how a departing / converting employee is closed off. A
// `null` date_end on the latest row means open-ended (continuous), so a trailing date_end is what stops
// the calc; contiguous rate changes (where only earlier rows carry a date_end) are unaffected.
export const getWeeklyHoursEndDate = (weeklyWorkHoursSchedule: Array<StaffWeeklyHours>) => {
    if (weeklyWorkHoursSchedule.length === 0) return null;
    const sortedSchedule = sortBy(weeklyWorkHoursSchedule, (entry) => entry.date_start);
    const latestRow = sortedSchedule[sortedSchedule.length - 1];
    const endDate = latestRow.date_end ? endOfDay(parseISO(latestRow.date_end)) : null;
    return endDate;
};

// The cumulative window end, capped at the working-relationship end when one is set. Used for the headline,
// the "Saldo até" caption and the modal so they all stop on the same day.
export const getEffectiveCumulativeToDate = (
    selectedMonthDate: Date,
    weeklyWorkHoursSchedule: Array<StaffWeeklyHours>,
) => {
    const weekAlignedEnd = getCumulativeToDate(selectedMonthDate);
    const periodEnd = getWeeklyHoursEndDate(weeklyWorkHoursSchedule);
    const toDate = periodEnd ? min([weekAlignedEnd, periodEnd]) : weekAlignedEnd;
    return toDate;
};

// default_clinic may arrive as a clinic id or, via a `*.*` fetch, a nested clinic object.
export const resolveClinicId = (value: unknown): number | null => {
    if (value == null) return null;
    if (typeof value === "number") return value;
    if (typeof value === "object" && "id" in (value as object)) {
        const id = (value as { id?: unknown }).id;
        return typeof id === "number" ? id : null;
    }
    return null;
};

type HolidayForClinic = { date: string; type: string; location?: number | string | null };

// National holidays apply to every clinic; a local holiday applies only to its own clinic.
// A null clinicId yields national holidays only.
export const buildClinicHolidaySet = (holidays: Array<HolidayForClinic>, clinicId: number | null) => {
    const dates = holidays
        .filter((holiday) => {
            if (holiday.type === "national") return true;
            if (holiday.type === "local" && clinicId != null) return String(holiday.location) === String(clinicId);
            return false;
        })
        .map((holiday) => format(utcToZonedTime(parseISO(holiday.date), TIMEZONE), "yyyy-MM-dd"));
    const set = new Set<string>(dates);
    return set;
};

// Planned hours for a given day: the rostered schedule hours that day, or — if none is rostered —
// the contract daily-equivalent (weekly_hours ÷ working days).
export const getPlannedHoursForDay = (
    date: Date,
    workLogs: Array<WorkLog>,
    weeklyWorkHoursSchedule: Array<StaffWeeklyHours>,
) => {
    const dayKey = format(date, "yyyy-MM-dd");
    const rosteredHours = workLogs
        .filter((log) => log.type === "schedules")
        .filter((log) => format(utcToZonedTime(parseISO(log.start_date), TIMEZONE), "yyyy-MM-dd") === dayKey)
        .reduce((sum, log) => sum + convertToHours(calculateHours(log.start_date, log.end_date)), 0);
    if (rosteredHours > 0) {
        return rosteredHours;
    }
    const dailyEquivalent = getDailyEquivalent(date, weeklyWorkHoursSchedule);
    return dailyEquivalent;
};

type TimeOffPlannedCheck = {
    enteredHours: number;
    date: Date;
    workLogs: Array<WorkLog>;
    weeklyWorkHoursSchedule: Array<StaffWeeklyHours>;
};

// Flags a sick/absence entry whose hours exceed the day's planned hours (drives a non-blocking warning).
export const checkTimeOffAgainstPlanned = ({ enteredHours, date, workLogs, weeklyWorkHoursSchedule }: TimeOffPlannedCheck) => {
    const plannedHours = getPlannedHoursForDay(date, workLogs, weeklyWorkHoursSchedule);
    const exceeds = enteredHours > plannedHours + 0.01;
    return { exceeds, plannedHours };
};

// Dec 25 and Jan 1 are full company closures, not a lost working Saturday, so they are excluded from the
// flexible Saturday give-back below. They are still credited on a weekday via the normal working-day skip.
const isClosureHoliday = (date: Date) =>
    (date.getMonth() === 11 && date.getDate() === 25) || (date.getMonth() === 0 && date.getDate() === 1);

// Sum the daily equivalent over each working day in [fromDate, toDate] that is not a public holiday or
// vacation. Working days come from the per-day day-off pattern; a holiday on a working day reduces expected,
// a holiday on a day off has no effect.
//
// Flexible staff are the exception: their day off rotates (unknown) but they work across Mon–Sat, so a public
// holiday on a Saturday gives back one daily-equivalent (benefit of the doubt — it may have been a working
// Saturday). Mon–Fri holidays are already given back by the working-day skip. Dec 25 / Jan 1 closures are
// excluded from the Saturday give-back (clinic closed regardless), but still credited on a weekday.
export const calculateCumulativeExpectedHours = (
    fromDate: Date,
    toDate: Date,
    weeklyWorkHoursSchedule: Array<StaffWeeklyHours>,
    vacationDays: Set<string>,
    publicHolidays: Set<string>,
) => {
    if (fromDate > toDate) return 0;

    const days = eachDayOfInterval({ start: fromDate, end: toDate });
    let cumulativeExpected = 0;

    for (const day of days) {
        const config = getApplicableDayOffConfig(day, weeklyWorkHoursSchedule);
        if (!config) continue;

        const dayKey = format(day, "yyyy-MM-dd");

        const isFlexibleSaturday = config.dayOffType === "flexible" && getDay(day) === 6;
        if (isFlexibleSaturday) {
            // The give-back compensates a week whose Mon–Fri base was charged. A window starting mid-week
            // (e.g. a contract starting ON a Saturday holiday) has no charged base — no give-back.
            const baseWeekIsInsideWindow = !isDateBefore(getWeekStartDate(day), startOfDay(fromDate));
            if (baseWeekIsInsideWindow && publicHolidays.has(dayKey) && !isClosureHoliday(day)) {
                cumulativeExpected -= getDailyEquivalent(day, weeklyWorkHoursSchedule);
            }
            continue;
        }

        if (!isWorkingDay(day, config)) continue;
        if (publicHolidays.has(dayKey)) continue;
        if (vacationDays.has(dayKey)) continue;

        cumulativeExpected += getDailyEquivalent(day, weeklyWorkHoursSchedule);
    }

    return cumulativeExpected;
};

// Vacation (holiday) credit for one day-entry: the ACTUAL entered hours on a creditable day (fixed →
// working days only; flexible → any day), else 0. Expected still charges the day's daily_equivalent, so a
// week nets to zero when rostered + vacation = the contracted week. This is the single source for the
// bag, the modal and the Férias widget, so the hours HR enters are exactly what everyone sees.
export const getHolidayCreditedHours = (
    entry: { start_date: string; end_date: string },
    weeklyWorkHoursSchedule: Array<StaffWeeklyHours>,
) => {
    const entryDate = parseISO(entry.start_date);
    const config = getApplicableDayOffConfig(entryDate, weeklyWorkHoursSchedule);
    if (!isTimeOffCreditableDay(entryDate, config)) return 0;
    const creditedHours = convertToHours(calculateHours(entry.start_date, entry.end_date));
    return creditedHours;
};

export const calculateCumulativeWorkedHours = (
    workLogs: Array<WorkLog>,
    timeOffs: Array<TimeOff>,
    fromDate: Date,
    toDate: Date,
    weeklyWorkHoursSchedule: Array<StaffWeeklyHours>,
) => {
    const scheduleHours = workLogs
        .filter((log) => log.type === "schedules")
        .filter((log) => isBetweenDates(parseISO(log.start_date), fromDate, toDate))
        .reduce((sum, log) => sum + convertToHours(calculateHours(log.start_date, log.end_date)), 0);

    const timeOffTypesWithActualHours = ["sick_leave", "missed_justified", "missed_not_justified"];
    const actualTimeOffHours = timeOffs
        .filter((entry) => timeOffTypesWithActualHours.includes(entry.type))
        .filter((entry) => isBetweenDates(parseISO(entry.start_date), fromDate, toDate))
        .reduce((sum, entry) => sum + convertToHours(calculateHours(entry.start_date, entry.end_date)), 0);

    const holidayHours = timeOffs
        .filter((entry) => entry.type === "holiday")
        .filter((entry) => isBetweenDates(parseISO(entry.start_date), fromDate, toDate))
        .reduce((sum, entry) => sum + getHolidayCreditedHours(entry, weeklyWorkHoursSchedule), 0);

    const paidTimeOffHours = actualTimeOffHours + holidayHours;
    const totalWorked = scheduleHours + paidTimeOffHours;
    return { totalWorked, scheduleHours, paidTimeOffHours };
};

export const calculateWorkedHoursByWeek = (workLogs: Array<WorkLog>, monthStart: Date, monthEnd: Date) => {
    const workedByWeek = new Map<string, number>();

    workLogs
        .filter((log) => log.type === "schedules")
        .filter((log) => isBetweenDates(parseISO(log.start_date), monthStart, monthEnd))
        .forEach((log) => {
            const logDate = parseISO(log.start_date);
            const weekStart = getWeekStartDate(logDate);
            const cappedWeekStart = isDateBefore(weekStart, monthStart) ? monthStart : weekStart;
            const weekKey = format(cappedWeekStart, "yyyy-MM-dd");
            const hours = convertToHours(calculateHours(log.start_date, log.end_date));
            const currentHours = workedByWeek.get(weekKey) ?? 0;
            workedByWeek.set(weekKey, currentHours + hours);
        });

    return workedByWeek;
};

type ComputeCumulativeHoursInput = {
    workLogs: Array<WorkLog>;
    timeOffs: Array<TimeOff>;
    toDate: Date;
    weeklyWorkHoursSchedule: Array<StaffWeeklyHours>;
    publicHolidaysSet: Set<string>;
    expectedHoursCorrection: number;
    extraHoursPaid: number;
    cumulativeStartDate: Date;
    employmentStartDate: Date | null;
    priorBalance?: number;
};

export const computeCumulativeHours = ({
    workLogs,
    timeOffs,
    toDate,
    weeklyWorkHoursSchedule,
    publicHolidaysSet,
    expectedHoursCorrection,
    extraHoursPaid,
    cumulativeStartDate,
    employmentStartDate,
    priorBalance = 0,
}: ComputeCumulativeHoursInput) => {
    if (weeklyWorkHoursSchedule.length === 0) {
        return { cumulativeExpected: 0, cumulativeWorked: 0, lifetimeSaldo: priorBalance };
    }

    const fromDate = resolveCumulativeFromDate(cumulativeStartDate, employmentStartDate);

    // Cap the window at the working-relationship end (latest weekly-hours row's date_end), so a departing
    // employee's expected and worked both stop there. No date_end → unchanged.
    const periodEnd = getWeeklyHoursEndDate(weeklyWorkHoursSchedule);
    const effectiveToDate = periodEnd ? min([toDate, periodEnd]) : toDate;

    const grossExpectedHours = calculateCumulativeExpectedHours(
        fromDate, effectiveToDate, weeklyWorkHoursSchedule, new Set<string>(), publicHolidaysSet
    );
    const cumulativeExpected = grossExpectedHours + expectedHoursCorrection;

    const cumulativeWorkedRaw = calculateCumulativeWorkedHours(workLogs, timeOffs, fromDate, effectiveToDate, weeklyWorkHoursSchedule);
    const cumulativeWorked = cumulativeWorkedRaw.totalWorked - extraHoursPaid;

    const lifetimeSaldo = priorBalance + (cumulativeWorked - cumulativeExpected);

    return { cumulativeExpected, cumulativeWorked, lifetimeSaldo };
};

export type WeeklyHoursBreakdown = {
    weekStart: string;
    weekStartDate: Date;
    weekEndDate: Date;
    scheduleHours: number;
    holidayHours: number;
    sickLeaveHours: number;
    missedHours: number;
    totalHours: number;
};

// Buckets worked/credited hours into whole Mon–Sun weeks across [fromDate, toDate]. Every week that
// intersects the window gets a row (so the weeks tile the window exactly and the totals match the
// headline), with the first and last weeks capped to the window. A month-straddling work-week therefore
// stays in a single row instead of being split at the month boundary.
export const groupHoursByWeek = (
    workLogs: Array<WorkLog>,
    timeOffs: Array<TimeOff>,
    weeklyWorkHoursSchedule: Array<StaffWeeklyHours>,
    fromDate: Date,
    toDate: Date,
) => {
    if (fromDate > toDate) return [] as Array<WeeklyHoursBreakdown>;

    const weeksMap = new Map<string, Omit<WeeklyHoursBreakdown, "totalHours">>();
    const weekMondays = eachWeekOfInterval({ start: fromDate, end: toDate }, { weekStartsOn: 1 });
    for (const monday of weekMondays) {
        const weekKey = format(monday, "yyyy-MM-dd");
        const weekStartDate = max([fromDate, monday]);
        const weekEndDate = min([toDate, getWeekEndDate(monday)]);
        weeksMap.set(weekKey, { weekStart: weekKey, weekStartDate, weekEndDate, scheduleHours: 0, holidayHours: 0, sickLeaveHours: 0, missedHours: 0 });
    }

    const weekKeyForDate = (date: Date) => format(getWeekStartDate(date), "yyyy-MM-dd");

    workLogs
        .filter((log) => log.type === "schedules")
        .filter((log) => isBetweenDates(parseISO(log.start_date), fromDate, toDate))
        .forEach((log) => {
            const week = weeksMap.get(weekKeyForDate(parseISO(log.start_date)));
            if (!week) return;
            week.scheduleHours += convertToHours(calculateHours(log.start_date, log.end_date));
        });

    timeOffs
        .filter((entry) => isBetweenDates(parseISO(entry.start_date), fromDate, toDate))
        .forEach((entry) => {
            const week = weeksMap.get(weekKeyForDate(parseISO(entry.start_date)));
            if (!week) return;
            if (entry.type === "holiday") {
                week.holidayHours += getHolidayCreditedHours(entry, weeklyWorkHoursSchedule);
            } else if (entry.type === "sick_leave") {
                week.sickLeaveHours += convertToHours(calculateHours(entry.start_date, entry.end_date));
            } else if (entry.type === "missed_justified" || entry.type === "missed_not_justified") {
                week.missedHours += convertToHours(calculateHours(entry.start_date, entry.end_date));
            }
        });

    const sortedWeeks = Array.from(weeksMap.values()).sort((a, b) => a.weekStart.localeCompare(b.weekStart));
    const result: Array<WeeklyHoursBreakdown> = sortedWeeks.map((week) => ({
        ...week,
        totalHours: week.scheduleHours + week.holidayHours + week.sickLeaveHours + week.missedHours,
    }));
    return result;
};

export type CumulativeWeeklyRow = WeeklyHoursBreakdown & {
    extraPaid: number;
    consideredHours: number;
    expectedHours: number;
    weekSaldo: number;
    runningSaldo: number;
};

export type RunAwareWeeklyRow = CumulativeWeeklyRow & {
    runLabel: string;
    isRunStart: boolean;
};

type BuildCumulativeWeeklyRowsInput = {
    weeklyBreakdown: Array<WeeklyHoursBreakdown>;
    extraHoursPaidByWeek: Map<string, number>;
    weeklyWorkHoursSchedule: Array<StaffWeeklyHours>;
    publicHolidaysSet: Set<string>;
    priorBalance: number;
};

export const buildCumulativeWeeklyRows = ({
    weeklyBreakdown,
    extraHoursPaidByWeek,
    weeklyWorkHoursSchedule,
    publicHolidaysSet,
    priorBalance,
}: BuildCumulativeWeeklyRowsInput) => {
    let runningSaldo = priorBalance;
    const rows: Array<CumulativeWeeklyRow> = weeklyBreakdown.map((week) => {
        const extraPaid = extraHoursPaidByWeek.get(week.weekStart) || 0;
        const consideredHours = week.totalHours - extraPaid;
        const expectedHours = calculateCumulativeExpectedHours(
            week.weekStartDate, week.weekEndDate, weeklyWorkHoursSchedule, new Set<string>(), publicHolidaysSet
        );
        const weekSaldo = consideredHours - expectedHours;
        runningSaldo += weekSaldo;
        return { ...week, extraPaid, consideredHours, expectedHours, weekSaldo, runningSaldo };
    });
    return rows;
};

type BuildRunAwareWeeklyRowsInput = {
    weeklyBreakdown: Array<WeeklyHoursBreakdown>;
    extraHoursPaidByWeek: Map<string, number>;
    weeklyWorkHoursSchedule: Array<StaffWeeklyHours>;
    publicHolidaysSet: Set<string>;
    priorBalance: number;
    runs: Array<ContractRun>;
    agreementTypeLabels?: Record<string, string>;
};

// The history-aware variant of buildCumulativeWeeklyRows: every week since the window start, labelled with
// its contract run. The running saldo RESETS to zero when the run changes — a class switch settles the
// previous relationship — so each run segment carries its own balance and the last segment matches the
// headline. priorBalance seeds the first listed segment (a snapshot always belongs to the current run,
// because an older one is discarded upstream).
export const buildRunAwareWeeklyRows = ({
    weeklyBreakdown,
    extraHoursPaidByWeek,
    weeklyWorkHoursSchedule,
    publicHolidaysSet,
    priorBalance,
    runs,
    agreementTypeLabels,
}: BuildRunAwareWeeklyRowsInput) => {
    let runningSaldo = priorBalance;
    let previousRunIndex: number | null = null;
    const rows: Array<RunAwareWeeklyRow> = weeklyBreakdown.map((week) => {
        const runIndex = resolveRunIndexForDate(week.weekEndDate, runs);
        const isRunStart = previousRunIndex !== null && runIndex !== previousRunIndex;
        if (isRunStart) {
            runningSaldo = 0;
        }
        previousRunIndex = runIndex;

        const extraPaid = extraHoursPaidByWeek.get(week.weekStart) || 0;
        const consideredHours = week.totalHours - extraPaid;
        const expectedHours = calculateCumulativeExpectedHours(
            week.weekStartDate, week.weekEndDate, weeklyWorkHoursSchedule, new Set<string>(), publicHolidaysSet
        );
        const weekSaldo = consideredHours - expectedHours;
        runningSaldo += weekSaldo;
        const runLabel = runs[runIndex] ? getRunLabel(runs[runIndex], agreementTypeLabels) : "—";
        return { ...week, extraPaid, consideredHours, expectedHours, weekSaldo, runningSaldo, runLabel, isRunStart };
    });
    return rows;
};

export const calculateTimeOffByWeek = (
    timeOffs: Array<TimeOff>,
    monthStart: Date,
    monthEnd: Date,
    weeklyWorkHoursSchedule: Array<StaffWeeklyHours>,
) => {
    const timeOffByWeek = new Map<string, Map<string, number>>();

    const relevantTypes = ["holiday", "sick_leave", "missed_justified", "missed_not_justified"];

    timeOffs
        .filter((entry) => relevantTypes.includes(entry.type))
        .filter((entry) => isBetweenDates(parseISO(entry.start_date), monthStart, monthEnd))
        .forEach((entry) => {
            const entryDate = parseISO(entry.start_date);
            const weekStart = getWeekStartDate(entryDate);
            const cappedWeekStart = isDateBefore(weekStart, monthStart) ? monthStart : weekStart;
            const weekKey = format(cappedWeekStart, "yyyy-MM-dd");

            const hours = entry.type === "holiday"
                ? getHolidayCreditedHours(entry, weeklyWorkHoursSchedule)
                : convertToHours(calculateHours(entry.start_date, entry.end_date));

            if (!timeOffByWeek.has(weekKey)) {
                timeOffByWeek.set(weekKey, new Map<string, number>());
            }
            const weekMap = timeOffByWeek.get(weekKey)!;
            const currentHours = weekMap.get(entry.type) ?? 0;
            weekMap.set(entry.type, currentHours + hours);
        });

    return timeOffByWeek;
};

